import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, DatePicker, Table, Spin, message } from 'antd';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import moment from 'moment';
import { useParams } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const { Option } = Select;
const { RangePicker } = DatePicker;

const Dashboard = () => {
  const { orgId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [timeRange, setTimeRange] = useState('last_7_days');
  const [customRange, setCustomRange] = useState(null);
  const [isCustom, setIsCustom] = useState(false);

  const fetchOrganizationData = async (timeRange, startDate, endDate) => {
    setLoading(true);
    let apiUrl = `/api/status/${orgId}?timeRange=${timeRange}`;

    if (timeRange === 'custom' && startDate && endDate) {
      apiUrl += `&startDate=${startDate}&endDate=${endDate}`;
    }

    try {
      const response = await axios.get(`${process.env.REACT_APP_API}${apiUrl}`);
      setData(response.data);
      setSelectedModel(response.data.modelUsage[0]?.modelName || ''); // Default to the first model
    } catch (error) {
      message.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCustom) {
      if (customRange) {
        const [start, end] = customRange;
        fetchOrganizationData('custom', start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
      }
    } else {
      fetchOrganizationData(timeRange);
    }
  }, [timeRange, customRange, isCustom]);

  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    setIsCustom(value === 'custom');
    setCustomRange(null);
  };

  const handleCustomRangeChange = (dates) => {
    setCustomRange(dates);
  };

  const handleModelChange = (value) => {
    setSelectedModel(value);
  };

  const lineChartOptions = (title) => ({
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: title },
    },
  });


  const timeSeriesOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Time Series for ${selectedModel}`,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'API Requests',
        },
        grid: {
          color: 'rgba(200, 200, 200, 0.1)',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Tokens',
        },
        grid: {
          drawOnChartArea: false, // No gridlines for secondary axis
        },
      },
      x: {
        grid: {
          color: 'rgba(200, 200, 200, 0.1)',
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data || !selectedModel) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '100px' }}>
        <p>No data available</p>
      </div>
    );
  }

  // Overall Token Trends
  const inputTokenTrendData = {
    labels: data.tokenTrends.inputTokens.map((point) => moment(point.date).format('MMM DD')),
    datasets: [
      {
        label: 'Input Tokens',
        data: data.tokenTrends.inputTokens.map((point) => point.value),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
      },
    ],
  };

  const outputTokenTrendData = {
    labels: data.tokenTrends.outputTokens.map((point) => moment(point.date).format('MMM DD')),
    datasets: [
      {
        label: 'Output Tokens',
        data: data.tokenTrends.outputTokens.map((point) => point.value),
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        fill: true,
      },
    ],
  };

  // Time Series for Selected Model
  const timeSeriesData = {
    labels: Object.keys(data.timeSeries).map((date) => moment(date).format('MMM DD')),
    datasets: [
      {
        label: 'API Requests',
        data: Object.keys(data.timeSeries).map(
          (date) => data.timeSeries[date][selectedModel]?.requests || 0
        ),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        yAxisID: 'y',
        fill: false,
      },
      {
        label: 'Input Tokens',
        data: Object.keys(data.timeSeries).map(
          (date) => data.timeSeries[date][selectedModel]?.inputTokens || 0
        ),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        yAxisID: 'y1',
        fill: true,
      },
      {
        label: 'Output Tokens',
        data: Object.keys(data.timeSeries).map(
          (date) => data.timeSeries[date][selectedModel]?.outputTokens || 0
        ),
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        yAxisID: 'y1',
        fill: true,
      },
    ],
  };

  // Model-Wise Token Usage
  const modelUsageData = {
    labels: data.modelUsage.map((model) => model.modelName),
    datasets: [
      {
        label: 'Input Tokens',
        data: data.modelUsage.map((model) => model.inputTokens),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
      },
      {
        label: 'Output Tokens',
        data: data.modelUsage.map((model) => model.outputTokens),
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        borderColor: 'rgba(153, 102, 255, 1)',
      },
    ],
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <Select value={timeRange} onChange={handleTimeRangeChange} style={{ width: '200px', marginRight: '20px' }}>
          <Option value="last_7_days">Last 7 Days</Option>
          <Option value="last_15_days">Last 15 Days</Option>
          <Option value="last_30_days">Last 30 Days</Option>
          <Option value="custom">Custom</Option>
        </Select>
        {isCustom && <RangePicker onChange={handleCustomRangeChange} />}
     
      </div>

      <Row gutter={24}>
        <Col span={12}>
          <Card title="Overall Input Tokens Trend">
            <Line data={inputTokenTrendData} options={lineChartOptions('Input Tokens')} />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Overall Output Tokens Trend">
            <Line data={outputTokenTrendData} options={lineChartOptions('Output Tokens')} />
          </Card>
        </Col>
      </Row>

     

      <Row gutter={24} style={{ marginTop: '20px' }}>
        <Col span={24}>
          <Card title="Model-Wise Token Usage">
            <Bar data={modelUsageData} options={lineChartOptions('Model-Wise Token Usage')} />
          </Card>
        </Col>
      </Row>


      <Row gutter={24} style={{ marginTop: '20px' }}>
        <div style={{display:"flex",justifyContent:"flex-end",width:"100%"}}>   <Select
          value={selectedModel}
          onChange={handleModelChange}
          style={{ width: '200px', marginLeft: '20px' ,marginBottom:"20px"}}
        >
          {data.modelUsage.map((model) => (
            <Option key={model.modelName} value={model.modelName}>
              {model.modelName}
            </Option>
          ))}
        </Select></div>
   
        <Col span={24}>
          <Card title={`Time Series for ${selectedModel}`}>
            <Line data={timeSeriesData} options={timeSeriesOptions} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
